import { Box, useDisclosure, useToast, VStack } from '@chakra-ui/react';
import { useDailyEvent, useMeetingState } from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { AppointmentStatus } from '@shared/types';
import { DebugTools } from '@web-app/components/CallV2/DebugTools/DebugTools';
import { DialOutModal } from '@web-app/components/CallV2/DialOutModal/DialOutModal';
import { DisclosureText } from '@web-app/components/CallV2/DisclosureText/DisclosureText';
import { EndCallModalV2 } from '@web-app/components/CallV2/EndCallModal/EndCallModalV2';
import { GracePeriodModal } from '@web-app/components/CallV2/EndCallModal/GracePeriodModal';
import { PostCallPanelV2 } from '@web-app/components/CallV2/PostCallPanel/PostCallPanelV2';
import { SidePane } from '@web-app/components/CallV2/SidePane/SidePane';
import { ChatNotificationSound } from '@web-app/components/Daily/ChatNotificationSound/ChatNotificationSound';
import { DailyPrebuilt } from '@web-app/components/Daily/DailyPrebuilt/DailyPrebuilt';
import TimeLimitNotificationModal from '@web-app/components/Daily/TimeLimitNotificationModal';
import { HeaderV2 } from '@web-app/components/HeaderV2/HeaderV2';
import { PreCallPayerActions } from '@web-app/components/PreCallPayerActions';
import PreCallTagActions from '@web-app/components/PreCallTagActions';
import { SessionReminderTrackingProvider } from '@web-app/components/SessionReminderTrackingProvider';
import ZocdocReminderModal from '@web-app/components/tagActions/ZocdocReminderModal';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { useProviderCallContext } from '@web-app/context/ProviderCallContext';
import { AppointmentResponse } from '@web-app/hooks/queries/appointment-by-room-name/types';
import { useApiClient } from '@web-app/hooks/useApiClient';
import { useDialOut } from '@web-app/hooks/useDialOut';
import { useEndAppointment } from '@web-app/hooks/useEndAppointment';
import { usePreCallRemindersEnabled } from '@web-app/hooks/usePreCallRemindersEnabled';
import { useGetSessionNote } from '@web-app/hooks/useSessionNote';
import { type EarlyCallEndReason } from '@web-app/utils/appointment/early-call-end-reason';
import {
  getAppointmentGracePeriodEnd,
  getAppointmentStartTime,
} from '@web-app/utils/appointment/grace-period';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { calculateSafeDuration } from '@web-app/utils/env.utils';
import { useEndCallGracePeriodEnabled } from '@web-app/utils/feature-flags/end-call-grace-period';
import { getAppointmentTags } from '@web-app/utils/patient-tags.utils';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';

import { PreCallJoinInfoUI } from '../PreCallJoinInfo/PreCallJoinInfoUI';
import { ResizeHandle } from './ResizeHandle';

interface ProviderCallContentProps {
  roomId: string;
  appointment: AppointmentResponse | undefined;
}

/**
 * Inner component that sits INSIDE DailyProvider and ProviderCallContextController.
 *
 * Can safely use:
 * - Daily hooks (useParticipantIds, useRecording, etc.)
 * - ProviderCallContext hooks (useProviderCallContext)
 *
 * Manages provider-specific business logic:
 * - Session notes UI
 * - End call modal and workflow
 * - Debug tools display
 *
 * Note: Appointment creation, duration tracking, and time limit notifications
 * are handled by ProviderCallContextController.
 */
export const ProviderCallContent: FC<ProviderCallContentProps> = ({
  roomId,
  appointment,
}) => {
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const toast = useToast();
  const apiClient = useApiClient();
  const isEndCallGracePeriodEnabled = useEndCallGracePeriodEnabled();

  // Read the note on demand: subscribing here re-renders the whole call page on every keystroke
  const getSessionNote = useGetSessionNote();

  // Check if debug mode is enabled via query string
  const isDebugMode = useMemo(() => {
    return router.query.debug === 'true';
  }, [router.query.debug]);

  // Check if mock transcript is enabled via query string (for e2e testing in staging)
  const useMockTranscript = useMemo(() => {
    return router.query.useMockTranscript === 'true';
  }, [router.query.useMockTranscript]);

  // Get provider call state from context (organized by domain)
  const { duration, ui, lifecycle } = useProviderCallContext();
  const { participantCount, hasStartTime } = lifecycle;
  const { calculatedDuration, editedDuration, editDuration, finalDuration } =
    duration;

  const appointmentUrl =
    typeof window !== 'undefined' ? window.location.href : '';
  const showPreCallReminders = usePreCallRemindersEnabled(
    appointment?.healthie?.appointment_type?.id ??
      appointment?.appointmentType?.id,
  );

  const { showTimeLimitModal, dismissTimeLimitModal } = ui;
  const {
    isModalOpen: isEndModalOpen,
    selectedStatus,
    isUpdatingStatus,
    apptOccurredOnThePhone,
    openModal: openEndModal,
    closeModal: closeEndModal,
    selectStatus,
    setApptOccurredOnThePhone,
    confirmEndCall,
    canConfirm: canConfirmEndCall,
    soapNoteJob,
    soapNoteError,
    isSoapNoteLoading,
    willGenerateSoapNotes,
  } = useEndAppointment({
    roomId,
    appointment,
    getSessionNote,
    useMockTranscript,
  });

  // Dial-out modal state and hook
  const {
    isOpen: isDialOutModalOpen,
    onOpen: openDialOutModal,
    onClose: closeDialOutModal,
  } = useDisclosure();
  const {
    startDialOut,
    stopDialOut,
    isDialOutActive,
    isConnecting: isDialOutConnecting,
    error: dialOutError,
    clearError: clearDialOutError,
  } = useDialOut(roomId);

  // A patient who already attended is not a late join, even if they leave first.
  const isWaitingForPatient =
    participantCount < 2 && !hasStartTime && !isDialOutActive;
  const [gracePeriodEndsAt, setGracePeriodEndsAt] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (!isWaitingForPatient) setGracePeriodEndsAt(null);
  }, [isWaitingForPatient]);

  const handleEndCallClick = useCallback(() => {
    const deadline =
      getAppointmentGracePeriodEnd(appointment?.healthie?.date) ??
      getAppointmentGracePeriodEnd(appointment?.healthie?.start);

    if (
      isEndCallGracePeriodEnabled &&
      isWaitingForPatient &&
      deadline !== null &&
      Date.now() < deadline
    ) {
      setGracePeriodEndsAt(deadline);
      return;
    }

    setGracePeriodEndsAt(null);
    openEndModal();
  }, [
    appointment?.healthie?.date,
    appointment?.healthie?.start,
    isEndCallGracePeriodEnabled,
    isWaitingForPatient,
    openEndModal,
  ]);

  const handleGracePeriodSelection = useCallback(
    (
      selection: 'keep_waiting' | 'end_call_anyway',
      reason?: EarlyCallEndReason,
      clickedAt = new Date().toISOString(),
    ) => {
      const appointmentStartTime =
        getAppointmentStartTime(appointment?.healthie?.date) ??
        getAppointmentStartTime(appointment?.healthie?.start);
      const minutesSinceAppointmentStart =
        appointmentStartTime === null
          ? null
          : calculateSafeDuration(
              new Date(appointmentStartTime),
              new Date(clickedAt),
            );

      datadogLogs.logger.info(
        'Grace period button clicked',
        callLogContext('end-appointment', {
          roomId,
          participantType: 'provider',
          appointmentId: appointment?.healthie_id,
          providerHealthieId: appointment?.provider_id,
          selection,
          appointmentStartsAt:
            appointmentStartTime === null
              ? null
              : new Date(appointmentStartTime).toISOString(),
          clickedAt,
          minutesSinceAppointmentStart,
          ...reason,
        }),
      );

      if (selection === 'keep_waiting') setGracePeriodEndsAt(null);
    },
    [
      roomId,
      appointment?.healthie_id,
      appointment?.provider_id,
      appointment?.healthie?.date,
      appointment?.healthie?.start,
    ],
  );

  const saveEarlyEndReason = useCallback(
    async (reason: EarlyCallEndReason) => {
      const clickedAt = new Date().toISOString();
      await apiClient.post('/appointments/end-early-reason', {
        roomName: roomId,
        reason,
      });
      handleGracePeriodSelection('end_call_anyway', reason, clickedAt);
    },
    [apiClient, roomId, handleGracePeriodSelection],
  );

  const handleDialOutConfirm = useCallback(
    async (phoneNumber: string) => {
      const defaultPhoneNumber = appointment?.user?.phoneNumber;
      const numberChanged = phoneNumber !== defaultPhoneNumber;

      trackEvent(EVENT_NAMES.DIAL_OUT_INITIATED, { numberChanged });

      const sessionId = await startDialOut(
        phoneNumber,
        appointment?.healthie?.user?.name,
      );
      // Close modal on successful connection (sessionId is non-null on success)
      if (sessionId) {
        closeDialOutModal();
      }
      // If sessionId is null, an error occurred and the hook sets the error state,
      // which will be displayed in the modal (it stays open)
    },
    [
      startDialOut,
      appointment?.healthie?.user?.name,
      appointment?.user?.phoneNumber,
      closeDialOutModal,
      trackEvent,
    ],
  );

  const handleDialOutModalClose = useCallback(() => {
    clearDialOutError();
    closeDialOutModal();
  }, [clearDialOutError, closeDialOutModal]);

  // Listen to custom button clicks from Daily's tray
  const handleCustomButtonClick = useCallback(
    (event: { button_id?: string } | null) => {
      if (event && 'button_id' in event) {
        if (event.button_id === 'endSession') {
          handleEndCallClick();
        } else if (event.button_id === 'dialOutPhone') {
          // If dial-out is active, hang up; otherwise open the modal to start a call
          if (isDialOutActive) {
            trackEvent(EVENT_NAMES.DIAL_OUT_HANGUP_CLICKED);
            // The dial-out modal is closed at this point, so its inline error
            // would never reach the provider.
            void stopDialOut().then((stopped) => {
              if (!stopped) {
                toast({
                  title: 'Could not hang up the phone call',
                  description:
                    'The phone line may still be connected. Ask the client to hang up, then try calling again.',
                  status: 'error',
                  duration: 8000,
                  isClosable: true,
                  position: 'top',
                });
              }
            });
          } else {
            clearDialOutError();
            openDialOutModal();
          }
        }
      }
    },
    [
      handleEndCallClick,
      openDialOutModal,
      clearDialOutError,
      isDialOutActive,
      stopDialOut,
      trackEvent,
      toast,
    ],
  );
  useDailyEvent('custom-button-click', handleCustomButtonClick);

  // Get current meeting state to detect when provider leaves call
  const meetingState = useMeetingState();
  const isPreJoinState = useMemo(() => {
    return (
      meetingState &&
      ['new', 'loading', 'loaded', 'joining-meeting'].includes(meetingState)
    );
  }, [meetingState]);

  // May or may not be joined, but before both participants have joined
  const haveBothParticipantsJoined = useMemo(() => {
    return participantCount < 2;
  }, [participantCount]);

  return (
    <VStack
      height="100vh"
      width="100vw"
      spacing={0}
      overflow="hidden"
      bg="bg.default"
      data-testid="provider-call-content"
    >
      {/* Header - Outside panels, no border */}
      <HeaderV2 appointment={appointment} />

      {/* Main Content - Resizable Panels */}
      <Box flex="1" width="100%" overflow="hidden">
        <PanelGroup
          direction="horizontal"
          autoSaveId="provider-call-content-horizontal"
        >
          {/* Left Panel - Notes, Goals & Patient (resizable, spans full height) */}
          <Panel defaultSize={40} minSize={25} maxSize={60}>
            <Box
              bg="bg.default"
              borderRadius="sm"
              boxShadow="sm"
              overflow="auto"
              position="relative"
              height="100%"
              pt={2}
            >
              {/* Debug Tools - Only shows when ?debug=true */}
              {isDebugMode && <DebugTools />}

              {/* Notes, Goals & Patient Tabbed Interface */}
              <SidePane appointment={appointment} roomId={roomId} />
            </Box>
          </Panel>

          <ResizeHandle direction="horizontal" />
          <Panel defaultSize={60} minSize={40}>
            <Box
              bg="surface.default"
              borderRadius="sm"
              overflow="hidden"
              boxShadow="sm"
              display="flex"
              flexDirection="column"
              height="100%"
            >
              {meetingState === 'left-meeting' ? (
                <PostCallPanelV2
                  job={soapNoteJob}
                  isPending={isSoapNoteLoading}
                  error={soapNoteError ?? null}
                  duration={finalDuration}
                  willGenerateSoapNotes={willGenerateSoapNotes}
                />
              ) : (
                <>
                  <Box flex="1" overflow="hidden" minHeight="0">
                    <DailyPrebuilt />
                  </Box>
                  {/* Disclosure text - only show in pre-join states */}
                  {isPreJoinState && (
                    <Box width="100%" data-testid="provider-disclosure-text">
                      <DisclosureText />
                    </Box>
                  )}
                  {/* Pre-call join info - this  */}
                  {/* {<PreCallJoinInfo appointment={appointment} />} */}
                  <PreCallJoinInfoUI
                    isOpen={haveBothParticipantsJoined}
                    patientName={appointment?.healthie?.user?.name}
                    appointmentUrl={appointmentUrl}
                    phoneNumber={appointment?.user?.phoneNumber}
                    phoneNumberFallback="no phone number on file"
                  />
                  {/* Spacer to prevent content from being hidden by the help chat button*/}
                  <Box height={16} bg="surface.subtleAlt" />{' '}
                </>
              )}

              <SessionReminderTrackingProvider
                key={roomId}
                roomId={roomId}
                appointmentId={
                  appointment?.healthie?.id ??
                  appointment?.healthie_id?.toString()
                }
              >
                {/* This reminder has its own prescriber eligibility. */}
                <ZocdocReminderModal
                  key={roomId}
                  tags={getAppointmentTags(appointment)}
                />

                {showPreCallReminders && (
                  <>
                    {/* Tag action modals - render once on component mount, user dismisses manually */}
                    <PreCallTagActions tags={getAppointmentTags(appointment)} />

                    {/* Payer action modals - render once on component mount, user dismisses manually */}
                    <PreCallPayerActions
                      policies={appointment?.healthie?.user?.policies}
                    />
                  </>
                )}
              </SessionReminderTrackingProvider>
            </Box>
          </Panel>
        </PanelGroup>
      </Box>

      {isEndCallGracePeriodEnabled &&
        isWaitingForPatient &&
        gracePeriodEndsAt !== null && (
          <GracePeriodModal
            gracePeriodEndsAt={gracePeriodEndsAt}
            onClose={() => setGracePeriodEndsAt(null)}
            onKeepWaiting={() => handleGracePeriodSelection('keep_waiting')}
            onSaveEndReason={saveEarlyEndReason}
            onEndCall={() => {
              setGracePeriodEndsAt(null);
              openEndModal();
            }}
            onExpired={() => {
              setGracePeriodEndsAt(null);
              openEndModal();
            }}
          />
        )}

      {/* End Call Modal - opened after the grace-period check */}
      <EndCallModalV2
        isOpen={isEndModalOpen}
        onClose={closeEndModal}
        selectedStatus={selectedStatus}
        onStatusChange={selectStatus}
        currentStatus={appointment?.status as AppointmentStatus | undefined}
        calculatedDuration={calculatedDuration}
        editedDuration={editedDuration}
        onDurationEdit={editDuration}
        apptOccurredOnThePhone={apptOccurredOnThePhone}
        onPhoneAppointmentChange={setApptOccurredOnThePhone}
        onConfirm={confirmEndCall}
        isPending={isUpdatingStatus}
        canConfirm={canConfirmEndCall}
      />

      <TimeLimitNotificationModal
        isOpen={showTimeLimitModal}
        onClose={dismissTimeLimitModal}
        appointment={appointment}
      />

      {/* Dial-out Modal - triggered by custom tray button */}
      <DialOutModal
        isOpen={isDialOutModalOpen}
        onClose={handleDialOutModalClose}
        onConfirm={handleDialOutConfirm}
        defaultPhoneNumber={appointment?.user?.phoneNumber}
        patientName={appointment?.healthie?.user?.name}
        error={dialOutError}
        isCalling={isDialOutConnecting}
      />

      {/* Chat notification sound - plays chime when messages are received */}
      <ChatNotificationSound />
    </VStack>
  );
};
